import React, { useState, useEffect, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '@lib/firebase';
import { useAuth } from '@features/auth';
import { PaymentModal } from '@features/payment';
import { getCourses, enrollmentServices } from '@features/courses';
import type { Course } from '@features/courses';

import styles from './CoursesPage.module.css';

import Card from '@/shared/ui/Card/Card';
import Button from '@/shared/ui/Button/Button';
import LoadingSpinner from '@/shared/ui/LoadingSpinner/LoadingSpinner';
import NotificationModal from '@/shared/ui/Modals/NotificationModal';
import { COURSE_IDS, COURSE_PRICING } from '@/shared/constants';

interface CourseCardProps {
    course: Course;
    isEnrolled: boolean;
    isDisabled: boolean;
    onEnroll: (courseId: string) => void;
    buttonText: string;
    isPriceTbd?: boolean;
}

const CourseCard = memo<CourseCardProps>(({ course, isEnrolled, isDisabled, onEnroll, buttonText, isPriceTbd = false }) => {
    return (
        <Card hoverable className={styles.courseCard}>
            {course.popular && (
                <div className={styles.popularBadge}>Most Popular</div>
            )}

            <div className={styles.cardContent}>
                <h3 className={styles.courseTitle}>{course.title}</h3>
                <p className={styles.courseDescription}>{course.description}</p>

                <div className={styles.priceSection}>
                    {isPriceTbd ? (
                        <span className={styles.price}>Pricing: TBD</span>
                    ) : (
                        <>
                            {course.originalPrice && (
                                <span className={styles.originalPrice}>
                                    ${Number(course.originalPrice).toFixed(2)}
                                </span>
                            )}
                            <span className={styles.price}>
                                ${Number(course.price).toFixed(2)}
                            </span>
                            {course.discount && (
                                <span className={styles.savings}>
                                    Save ${course.discount}!
                                </span>
                            )}
                        </>
                    )}
                </div>

                <ul className={styles.features}>
                    {(course.features as string[] || []).map((feature, index) => (
                        <li key={index}>
                            <span className={styles.checkmark}>✓</span>
                            {feature}
                        </li>
                    ))}
                </ul>

                {isEnrolled && (
                    <div className={styles.enrolledBadge}>
                        ✓ Enrolled
                    </div>
                )}

                <Button
                    variant={course.popular ? 'primary' : 'outline'}
                    onClick={() => onEnroll(course.id)}
                    disabled={isDisabled}
                    className={styles.enrollButton}
                >
                    {buttonText}
                </Button>
            </div>
        </Card>
    );
});

CourseCard.displayName = 'CourseCard';

const CoursesPage: React.FC = () => {
    const navigate = useNavigate();
    const { user, userProfile } = useAuth();
    const [courses, setCourses] = useState<Course[]>([]);
    const [enrollments, setEnrollments] = useState<Record<string, any>>({});
    const [loading, setLoading] = useState(true);
    const [selectedCourse, setSelectedCourse] = useState<string | null>(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showPrerequisiteWarning, setShowPrerequisiteWarning] = useState(false);
    const [pendingCourseId, setPendingCourseId] = useState<string | null>(null);
    const [showTbdNotice, setShowTbdNotice] = useState(false);
    const isPriceTbd = import.meta.env.VITE_PRICE_TBD === 'true';

    const loadCourses = useCallback(async () => {
        try {
            setLoading(true);

            const coursesData = await getCourses();
            setCourses(coursesData);
        } catch (error) {
            console.error('Error loading courses:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Real-time enrollment listener
    useEffect(() => {
        if (!user) {
            setEnrollments({});
            return;
        }

        const enrollmentsRef = collection(db, 'users', user.uid, 'enrollments');
        const unsubscribe = onSnapshot(enrollmentsRef, (snapshot) => {
            const enrollmentData: Record<string, any> = {};
            snapshot.docs.forEach(doc => {
                enrollmentData[doc.id] = { id: doc.id, ...doc.data() };
            });
            setEnrollments(enrollmentData);
        }, (error) => {
            console.error('Error syncing enrollments:', error);
        });

        return () => unsubscribe();
    }, [user]);

    useEffect(() => {
        loadCourses();
        // eslint_disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                loadCourses();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
        // eslint_disable-next-line react-hooks/exhaustive-deps
    }, [loadCourses]);

    const isEnrolled = useCallback((courseId: string) => {
        return enrollments[courseId] ? true : false;
    }, [enrollments]);

    const isButtonDisabled = useCallback((courseId: string) => {
        // If parental consent is pending, disable all enrollment buttons
        if (userProfile?.parentConsent?.status === 'pending') {
            return true;
        }

        // If user already has this course, it's not disabled (allows "View in Dashboard" click)
        if (enrollments[courseId]) return false;

        // Check if user has any conflicting enrollment
        const hasOnline = enrollments[COURSE_IDS.ONLINE];
        const hasBehindWheel = enrollments[COURSE_IDS.BEHIND_WHEEL];
        const hasComplete = enrollments[COURSE_IDS.COMPLETE];

        // If user has Complete package, disable enrolling in individual components
        if (hasComplete) return true;

        // If user has Online AND trying to enroll in Behind-Wheel or vice versa
        if ((hasOnline || hasBehindWheel) && courseId === COURSE_IDS.COMPLETE) {
            return true;
        }

        return false;
    }, [enrollments, userProfile]);

    const handlePaymentSuccess = useCallback(async (paymentData: any) => {
        try {
            // Payment is already confirmed by CheckoutForm
            if (!paymentData.paymentConfirmed) {
                throw new Error('Payment was not confirmed');
            }

            // Enrollment will be created by webhook after payment confirmation
            // This prevents double-charging from webhook adding to existing amountPaid
            if (import.meta.env.VITE_USE_EMULATORS === 'true' && user) {
                console.warn('[EMULATOR] Manually creating enrollment to bypass missing webhook...');
                await enrollmentServices.createPaidEnrollment(user.uid, paymentData.courseId, paymentData.amount || 0);
            }

            // Reload courses to reflect new enrollment
            await loadCourses();

            // Navigate to payment success page
            setTimeout(() => {
                navigate(`/payment-success?courseId=${paymentData.courseId}`);
            }, 500);
        } catch (error: any) {
            console.error('Error processing payment:', error);
            alert('Payment failed: ' + error.message);
        }
    }, [loadCourses, navigate]);

    const proceedWithEnrollment = useCallback(async (courseId: string) => {
        const course = courses.find(c => c.id === courseId);
        // If course is free (price 0), enroll directly without payment modal
        if (course && Number(course.price) === 0) {
            try {
                setLoading(true);
                // Create enrollment directly for free courses since there is no webhook
                if (user) {
                    await enrollmentServices.createEnrollment(user.uid, courseId, user.email || '');

                    // Simulate payment success with 0 amount
                    await handlePaymentSuccess({
                        courseId,
                        amount: 0,
                        paymentOption: 'full', // Default to full for free
                        id: 'free_' + Date.now(), // Dummy payment ID
                        paymentConfirmed: true
                    });
                }
            } catch (error) {
                console.error('Free enrollment failed:', error);
            } finally {
                setLoading(false);
            }
            return;
        }

        // Show payment modal
        setSelectedCourse(courseId);
        setShowPaymentModal(true);
    }, [courses, user, handlePaymentSuccess]);

    const handleEnroll = useCallback(async (courseId: string) => {
        if (!user) {
            navigate('/login', { state: { from: '/courses' } });
            return;
        }

        // Require email verification before enrollment (skipped in emulator)
        if (!user.emailVerified && import.meta.env.VITE_USE_EMULATORS !== 'true') {
            navigate('/verify-email');
            return;
        }

        // Block enrollment if parental consent is pending
        if (userProfile?.parentConsent?.status === 'pending') {
            navigate('/dashboard');
            return;
        }

        // Check if already enrolled or disabled
        if (isEnrolled(courseId) || isButtonDisabled(courseId)) {
            navigate('/dashboard');
            return;
        }

        if (isPriceTbd) {
            setShowTbdNotice(true);
            return;
        }

        // Check if user has BTW and is trying to enroll in Online course
        // BTW requires online completion, so show warning if they have BTW
        const hasBehindWheel = enrollments[COURSE_IDS.BEHIND_WHEEL];
        if (hasBehindWheel && courseId === COURSE_IDS.ONLINE) {
            setShowPrerequisiteWarning(true);
            setPendingCourseId(courseId);
            return;
        }

        proceedWithEnrollment(courseId);
    }, [user, userProfile, navigate, enrollments, isEnrolled, isButtonDisabled, proceedWithEnrollment, isPriceTbd]);

    const getButtonText = useCallback((course: Course) => {
        if (!user) {
            return isPriceTbd ? 'Sign In to Register' : 'Sign In to Enroll';
        }

        if (userProfile?.parentConsent?.status === 'pending') {
            return 'Parental Consent Required';
        }

        if (isEnrolled(course.id)) {
            return 'View in Dashboard';
        }

        if (isButtonDisabled(course.id)) {
            return isPriceTbd ? 'Already Registered' : 'Already Enrolled';
        }

        if (isPriceTbd) {
            return 'Register';
        }

        const pricing = COURSE_PRICING[course.id as keyof typeof COURSE_IDS];
        const upfrontPrice = pricing ? pricing.upfront : course.price;
        return `Enroll - $${(Number(upfrontPrice) || 0).toFixed(2)}`;
    }, [user, userProfile, isEnrolled, isButtonDisabled, isPriceTbd]);

    if (loading) {
        return <LoadingSpinner fullScreen text="Loading courses..." />;
    }

    return (
        <div className={styles.coursesPage}>
            <div className={styles.container}>
                <div className={styles.header}>
                    <h1 className={styles.title}>Our Courses</h1>
                    <p className={styles.subtitle}>
                        Choose from our comprehensive driving courses designed to help you become a safe and confident driver
                    </p>
                </div>

                <div className={styles.grid}>
                    {courses.map((course) => (
                        <CourseCard
                            key={course.id}
                            course={course}
                            isEnrolled={isEnrolled(course.id)}
                            isDisabled={isButtonDisabled(course.id)}
                            onEnroll={handleEnroll}
                            buttonText={getButtonText(course)}
                            isPriceTbd={isPriceTbd}
                        />
                    ))}
                </div>

                <div className={styles.infoSection}>
                    <h2>Why Choose Fastrack Driving School?</h2>
                    <div className={styles.infoGrid}>
                        <div className={styles.infoCard}>
                            <div className={styles.infoIcon}>🎓</div>
                            <h3>BMV Certified</h3>
                            <p>All our courses are fully certified by the Ohio Bureau of Motor Vehicles</p>
                        </div>
                        <div className={styles.infoCard}>
                            <div className={styles.infoIcon}>👨‍🏫</div>
                            <h3>Expert Instructors</h3>
                            <p>Learn from experienced, certified driving instructors</p>
                        </div>
                        <div className={styles.infoCard}>
                            <div className={styles.infoIcon}>⏰</div>
                            <h3>Flexible Schedule</h3>
                            <p>Learn at your own pace with our online courses</p>
                        </div>
                        <div className={styles.infoCard}>
                            <div className={styles.infoIcon}>💯</div>
                            <h3>High Success Rate</h3>
                            <p>Our students consistently pass their driving tests</p>
                        </div>
                    </div>
                </div>
            </div>

            {showPrerequisiteWarning && (
                <div className={styles.modalOverlay} onClick={() => {
                    setShowPrerequisiteWarning(false);
                    setPendingCourseId(null);
                }}>
                    <div className={styles.warningModal} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.warningHeader}>
                            <h2>Online Course Prerequisites</h2>
                        </div>
                        <div className={styles.warningContent}>
                            <p className={styles.warningIcon}>⚠️</p>
                            <p>
                                We noticed you have already purchased the Behind-the-Wheel course. This course requires completion of the online course as a prerequisite.
                            </p>
                            <p>
                                You should already have completion proof from Fastrack or another provider. Are you sure you want to enroll in the online course again?
                            </p>
                        </div>
                        <div className={styles.warningActions}>
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setShowPrerequisiteWarning(false);
                                    setPendingCourseId(null);
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => {
                                    setShowPrerequisiteWarning(false);
                                    if (pendingCourseId) {
                                        proceedWithEnrollment(pendingCourseId);
                                    }
                                }}
                            >
                                Yes, Enroll Anyway
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {selectedCourse && (
                <PaymentModal
                    isOpen={showPaymentModal}
                    onClose={() => {
                        setShowPaymentModal(false);
                        setSelectedCourse(null);
                        loadCourses();
                    }}
                    amount={COURSE_PRICING[selectedCourse as keyof typeof COURSE_IDS]?.upfront || Number(courses.find(c => c.id === selectedCourse)?.price) || 0}
                    courseId={selectedCourse}
                    courseName={courses.find(c => c.id === selectedCourse)?.title}
                    paymentType="upfront"
                    onSuccess={handlePaymentSuccess}
                />
            )}

            <NotificationModal
                isOpen={showTbdNotice}
                onClose={() => setShowTbdNotice(false)}
                title="Registration Pending"
                type="warning"
                buttonText="Got It"
                message="Fastrack Driving School is currently undergoing final licensure review with the Ohio BMV. Registration and payment features are temporarily locked during this audit period. Please check back soon!"
            />
        </div>
    );
};

export default CoursesPage;
